import { BillingDetails } from '@app/domain/BillingDetails'
import { BillingDetailsDTO } from '@services/DTO/BillingDetailsDTO'
import { DOCUMENT } from '@angular/common'
import { DTOAdapter } from '@services/DTOAdapter'
import { HttpClient } from '@angular/common/http'
import { Inject, Injectable, RendererFactory2 } from '@angular/core'
import { Observable, finalize, firstValueFrom, map, of, shareReplay, tap } from 'rxjs'
import { WindowProvider } from '@app/helpers/providers/window.provider'
import { environment } from '@environments/environment'

declare let Chargebee: any

interface ChargebeeHostedPageDTO {
    url?: string
}

interface BillingCheckoutResponseDTO {
    hostedPage?: ChargebeeHostedPageDTO
    hosted_page?: ChargebeeHostedPageDTO
    url?: string
}

@Injectable({
    providedIn: 'root',
})
export class BillingService {

    cbInstance: any
    private chargebeeScript = 'https://js.chargebee.com/v2/chargebee.js'
    private billingDetailsCache: BillingDetails | null = null
    private billingDetailsCacheTime: number | null = null
    private billingDetailsRequest: Observable<BillingDetails> | null = null
    private cacheDurationMilliseconds: number = 5 * 60 * 1000

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private windowProvider: WindowProvider,
        rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) document: Document
    ) {
        const renderer = rendererFactory.createRenderer(null, null)
        const script = renderer.createElement('script')
        script.type = 'text/javascript'
        script.src = this.chargebeeScript
        renderer.appendChild(document.body, script)
        script.onload = () => {
            this.cbInstance = Chargebee.init({
                site: environment.chargebeeSite,
                publishableKey: environment.chargebeeKey,
            })
        }
    }

    openCheckoutTrialPortal(onSuccess: () => void) {
        this.cbInstance.logout()
        this.cbInstance.openCheckout({
            hostedPage: () => {
                return firstValueFrom(this.createTrialSubscriptionCheckout())
            },
            success: () => {
                onSuccess()
            },
        })
    }

    getTrialSubscriptionCheckoutUrl(): Observable<string> {
        return this.createTrialSubscriptionCheckout()
            .pipe(
                map(checkout => this.getCheckoutUrl(checkout))
            )
    }

    openTrialSubscriptionCheckoutUrl(checkoutUrl: string) {
        this.windowProvider.navigateToUrl(checkoutUrl)
    }

    openTrialSubscriptionCheckoutHostedPage(): Observable<void> {
        return this.getTrialSubscriptionCheckoutUrl()
            .pipe(
                tap(checkoutUrl => {
                    this.openTrialSubscriptionCheckoutUrl(checkoutUrl)
                }),
                map(() => undefined)
            )
    }

    openManageSubscriptionPortal(onClose: () => void) {
        this.cbInstance.logout()
        this.cbInstance.setPortalSession(() => {
            return firstValueFrom(this.createPortalSession())
        })
        const portal = this.cbInstance.createChargebeePortal()
        portal.open({
            close: () => {
                onClose()
            },
        })
    }

    getBillingDetails(): Observable<BillingDetails> {
        const url = new URL('/organisation/billing', environment.apiBaseURL)
        return this.http.get<BillingDetailsDTO>(url.toString())
            .pipe(
                map(dto => this.dtoAdapter.adaptBillingDetailsDto(dto)),
                tap(billingDetails => {
                    this.billingDetailsCache = billingDetails
                    this.billingDetailsCacheTime = Date.now()
                })
            )
    }

    getCachedBillingDetails(): Observable<BillingDetails> {
        const now = Date.now()
        const isCacheValid = this.billingDetailsCacheTime && (now - this.billingDetailsCacheTime < this.cacheDurationMilliseconds)
        if (isCacheValid) {
            const cachedBillingDetails = this.billingDetailsCache
            if (cachedBillingDetails) {
                return of(cachedBillingDetails)
            }
        }
        if (this.billingDetailsRequest === null) {
            this.billingDetailsRequest = this.getBillingDetails()
                .pipe(
                    finalize(() => {
                        this.billingDetailsRequest = null
                    }),
                    shareReplay({
                        bufferSize: 1,
                        refCount: false,
                    })
                )
        }
        return this.billingDetailsRequest
    }

    private createTrialSubscriptionCheckout(): Observable<BillingCheckoutResponseDTO> {
        const url = new URL('/organisation/billing/trial/subscription', environment.apiBaseURL)
        return this.http.post<BillingCheckoutResponseDTO>(url.toString(), null)
    }

    private createPortalSession(): Observable<any> {
        const url = new URL('/organisation/billing/subscription/session', environment.apiBaseURL)
        return this.http.post<any>(url.toString(), null)
    }

    private getCheckoutUrl(checkout: BillingCheckoutResponseDTO): string {
        const checkoutUrl = checkout.hostedPage?.url
            ?? checkout.hosted_page?.url
            ?? checkout.url
        if (checkoutUrl === undefined) {
            throw new Error('Subscription checkout URL was not returned')
        }
        return checkoutUrl
    }
}
